import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IPSDFilter, IPSDFilterList, IPSDFilterOptions } from '../../../../types/psd.types';
import { getPsdFilters, getTopPsdData } from '../../../../requests/public-psd.request';
import { useKato } from '../../../../contexts/kato.context';
import ReactSelect from 'react-select';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import classes from './styles.module.scss';
import Card from '../../../../components/card/card.component';

const filterNames: (keyof IPSDFilter)[] = [
  'projectName', 'customer', 'objectLocation', 'examCompany',
  'bin', 'financingSource', 'designer', 'conclusionNumber'
];

const keyToListKey: Record<string, keyof IPSDFilterList> = {
  projectName: 'projectNames',
  customer: 'customers',
  objectLocation: 'objectLocations',
  examCompany: 'examLocations',
  bin: 'bins',
  financingSource: 'financingSources',
  designer: 'designers',
  conclusionNumber: 'conclusionNumbers'
};

const defaultFilter: IPSDFilter = {
  projectName: '',
  customer: '',
  objectLocation: '',
  examCompany: '',
  bin: '',
  financingSource: '',
  designer: '',
  conclusionNumber: '',
  startDate: '',
  endDate: ''
};

const TenPsdTab = () => {
  const { t } = useTranslation();
  const { getCurrentKato } = useKato();
  const [data, setData] = useState<{ client: any[]; category: any[] }>({ client: [], category: [] });
  const [filters, setFilters] = useState<IPSDFilter>(defaultFilter);
  const [filterDict, setFilterDict] = useState<IPSDFilterOptions>({
    bins: [], conclusionNumbers: [], customers: [], designers: [],
    financingSources: [], objectLocations: [], projectNames: [],
    examLocations: [], minDate: '', maxDate: ''
  });

  useEffect(() => {
    const katoValue = getCurrentKato()?.kato;
    if (!katoValue) return;

    getTopPsdData(+katoValue, filters).then((res) => {
      setData({
        client: res.client
          .sort((a, b) => b.count - a.count)
          .slice(0, 10)
          .map((item) => ({
            shortLabel: item.client.length > 20 ? item.client.slice(0, 20) + '...' : item.client,
            fullLabel: item.client,
            count: item.count,
          })),
        category: res.category.sort((a, b) => b.count - a.count),
      });
      getPsdFilters(+katoValue, filters).then((res) => {
        const makeOptions = (arr: string[]) =>
          [...arr.filter(Boolean)].map((value) => ({
            value,
            label: t(value) || t('psd-page.all-options')
          }));

        setFilterDict({
          bins: makeOptions(res.bins),
          conclusionNumbers: makeOptions(res.conclusionNumbers),
          customers: makeOptions(res.customers),
          designers: makeOptions(res.designers),
          financingSources: makeOptions(res.financingSources),
          objectLocations: makeOptions(res.objectLocations),
          projectNames: makeOptions(res.projectNames),
          examLocations: makeOptions(res.examLocations),
          minDate: res.minDate[0],
          maxDate: res.maxDate[0]
        });
      });
    });
  }, [filters, getCurrentKato]);

  const handleFilterChange = (name: keyof IPSDFilter, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className={classes['ten-psd']}>
      <div className={classes['ten-psd__filters']}>

        {/* Дата */}
        <div className={classes['ten-psd__filter']}>
          <span>{t('cards.psd-card.filters.period')}</span>
          <div className={classes['ten-psd__dates']}>
            <input
              type="date"
              min={filterDict.minDate}
              max={filterDict.maxDate}
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
            -
            <input
              type="date"
              min={filterDict.minDate}
              max={filterDict.maxDate}
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
        </div>

        {/* Селекты */}
        {filterNames.map((filterName) => {
          const optionsRaw = filterDict[keyToListKey[filterName]];
          const options = Array.isArray(optionsRaw) ? optionsRaw : [];
          const selected = options.find((opt) => opt.value === filters[filterName]) || null;

          return (
            <div className={classes['ten-psd__filter']} key={filterName}>
              <span>{t(`cards.psd-card.filters.${filterName}`)}</span>
              <ReactSelect
                className={classes['ten-psd__filter__select']}
                options={options}
                value={selected}
                onChange={(option) => handleFilterChange(filterName, option?.value || '')}
                placeholder={t('cards.psd-card.filters.all-options')}
                isClearable
                isSearchable
              />
            </div>
          );
        })}
      </div>

      <Card title={t('cards.psd-card.top-clients.title')} subtitle={t('cards.psd-card.top-clients.subtitle')}>
        <ResponsiveContainer width="100%" height={'100%'}>
          <BarChart
            layout="vertical"
            data={data.client}
            barSize={9}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis type="number" hide />
            <YAxis dataKey="shortLabel" type="category" tick={({ x, y, payload }) => (
              <text
                x={x - 140}
                y={y + 4}
                fontSize={'0.75rem'}
                fontWeight={400}
                fill="#202224"
                textAnchor="start"
              >
                {payload.value}
              </text>
            )}
              width={150}
            />
            <Tooltip
              content={({ payload }) => {
                if (!payload || !payload.length) return null;
                const item = payload[0]?.payload;
                if (!item) return null;

                return (
                  <div style={{
                    background: '#fff',
                    border: '1px solid #ccc',
                    padding: '0.5rem',
                    fontSize: '0.75rem',
                    maxWidth: 300,
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}>
                    <div><strong>{item.fullLabel}</strong></div>
                    <div style={{ color: 'var(--color-primary)', marginTop: '0.625rem' }}><strong>{item.count}</strong></div>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" fill="var(--color-primary)" radius={[0, 4, 4, 0]}>
              <LabelList
                dataKey="count"
                position="right"
                style={{
                  fontSize: '0.875rem',
                  fill: 'var(--color-primary)',
                  fontWeight: 700,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card title={t('cards.psd-card.top-categories.title')} subtitle={t('cards.psd-card.top-categories.subtitle')}>
        <ResponsiveContainer width="100%" height={'100%'}>
          <BarChart
            layout="vertical"
            data={data.category}
            barSize={9}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis type="number" hide />
            <YAxis dataKey="category" type="category" width={150} tick={({ x, y, payload }) => (
              <text
                x={x - 140}
                y={y + 4}
                fontSize={'0.75rem'}
                fontWeight={400}
                fill="#202224"
                textAnchor="start"
                style={{ wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: 40 }}
              >
                {payload.value}
              </text>
            )} />
            <Tooltip />
            <Bar dataKey="count" fill="var(--color-primary)" radius={[0, 4, 4, 0]}>
              <LabelList
                dataKey="count"
                position="right"
                style={{
                  fontSize: '0.875rem',
                  fill: 'var(--color-primary)',
                  fontWeight: 700,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default TenPsdTab;
